import { createContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RouteConstants from "../../../constants/routeConstants.json";
import ContentData from "../../../constants/contentFile.json";
import { addBatteryApi, addInverterApi } from "../../../api/Api";
import { handleApiError } from "../../../constants/Constants";

export const validateField = (name, value) => {
  let error = "";
  const formattedName = name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

  // Check if value is undefined or null first
  if (value === undefined || value === null) {
    return `${formattedName} is required`;
  }

  // Convert value to string before using trim()
  const stringValue = String(value);
  if (stringValue?.trim() === "") {
    error = `${formattedName} is required`;
  }

  // Additional field-specific validations
  switch (name) {
    case "power":
      if (stringValue?.trim() !== "" && !/^\d+$/.test(stringValue)) {
        error = "Power must be a number";
      }
      break;
    case "powerInVoltAmperes":
      if (stringValue?.trim() !== "" && !/^\d+$/.test(stringValue)) {
        error = "Power must be a number";
      }
      break;
    case "volt":
      if (stringValue?.trim() !== "" && !/^\d+$/.test(stringValue)) {
        error = "Voltage must be a number";
      }
      break;
    case "capacity":
      if (stringValue?.trim() !== "" && !/^\d+$/.test(stringValue)) {
        error = "Capacity must be a number";
      }
      break;
    case "productLink":
      if (stringValue?.trim() !== "" && !stringValue.startsWith("http")) {
        error = "Please enter a valid URL starting with http/https";
      }
      break;
    case "type":
      if (stringValue?.trim() !== "" && !/^[A-Za-z\s]+$/.test(stringValue)) {
        error = "Enter a valid type";
      }
      break;
  }
  return error;
};

export const AddProductContext = createContext({});
export const AddProductContextProvider = ({ children }) => {
  const [formData, setFormData] = useState({
    model: "",
    power: "",
    powerInVoltAmperes: "",
    volt: "",
    type: "",
    capacity: "",
    connectivity: "Yes",
    solarModal: "Yes",
    productLink: "",
    siteOrgin: "vguard.com",
  });
  const [errors, setErrors] = useState({
    model: "",
    power: "",
    powerInVoltAmperes: "",
    volt: "",
    type: "",
    capacity: "",
    connectivity: "",
    solarModal: "",
    productLink: "",
    siteOrgin: "",
  });
  const [spinnerStatus, setSpinnerStatus] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [imgError, setImgError] = useState("");
  const currentTab = localStorage.getItem("tabValue");
  const [tabValue, setTabValue] = useState(currentTab ? currentTab : "1");
  const [successAlertShow, setSuccessAlertShow] = useState(false);
  const [successAlertMessage, setSuccessAlertMessage] = useState("");
  const [statusValue, setStatusValue] = useState(0);
  const [errorPopup, setErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [buttonText, setButtonText] = useState("");

  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Fields to validate based on current tab
    const fieldsToValidate =
      tabValue === "1"
        ? [
            "model",
            "power",
            "powerInVoltAmperes",
            "volt",
            "type",
            "connectivity",
            "solarModal",
            "productLink",
            "siteOrgin",
          ]
        : ["model", "capacity", "productLink", "siteOrgin"];

    fieldsToValidate.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        isValid = false;
        newErrors[field] = error;
      }
    });

    // Validate image
    if (!selectedFile) {
      isValid = false;
      setImgError("Product image is required");
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue =
      type === "radio" ? (checked ? value : formData[name]) : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    // Validate field on change
    const error = validateField(name, newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleTabChange = (_event, newValue) => {
    setTabValue(newValue);
    // Clear errors when switching tabs
    setErrors({});
    setFormData({});
    setImgError("");
  };
  const addProduct = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setSpinnerStatus(true);

      try {
        // FormData to send the image as binary
        const formDataToSend = new FormData();
        formDataToSend.append("modelName", formData.model);
        formDataToSend.append(
          "siteOrigin",
          formData.siteOrgin === "vguard.com" ? "com" : "in"
        );
        formDataToSend.append("url", formData.productLink);

        if (tabValue === "1") {
          formDataToSend.append("power", formData.power);
          formDataToSend.append("powerInVoltAmperes", formData.powerInVoltAmperes);
          formDataToSend.append("batteryVoltage", formData.volt);
          formDataToSend.append("type", formData.type);
          formDataToSend.append(
            "connectivity",
            formData.connectivity === "Yes" ? true : false
          );
          formDataToSend.append(
            "solarModel",
            formData.solarModal === "Yes" ? true : false
          );
        } else {
          formDataToSend.append("capacity", formData.capacity);
        }
        if (selectedFile) {
          formDataToSend.append("image", selectedFile);
        }

        // API based on tab selection
        const response =
          tabValue === "1"
            ? await addInverterApi(formDataToSend)
            : await addBatteryApi(formDataToSend);

        if (response.data.statusCode === 200) {
          setSpinnerStatus(false);
          setSuccessAlertShow(true);
          setSuccessAlertMessage(response?.data?.message);
          setErrorPopup(false);
        }
      } catch (error) {
        handleApiError(
          error,
          setStatusValue,
          setErrorPopupMessage,
          setErrorPopup,
          setButtonText
        );
      }
      setSpinnerStatus(false);
    }
  };

  const onImageChange = (file) => {
    if (file) {
      if (file?.size > 2 * 1024 * 1024) {
        setImgError("File size exceeds 2MB limit");
        return;
      } else {
        setSelectedFile(file);
        const tempUrl = URL.createObjectURL(file);
        setFileUrl(tempUrl);
        setImgError("");
      }
    }
  };

  const clearImage = () => {
    setSelectedFile(null);
    if (fileUrl && fileUrl.startsWith("blob:")) {
      URL.revokeObjectURL(fileUrl);
    }
    setFileUrl(null);
    setImgError("");
  };

  const goToProductList = () => {
    navigate(RouteConstants.LIST_PRODUCTS);
  };

  const uploadToServer = (file) => {
    const formData = new FormData();
    formData.append("image", file);
  };
  const goToLogin = () => {
    navigate(RouteConstants.LOGIN);
  };

  const providerValue = {
    spinnerStatus,
    goToProductList,
    addProduct,
    formData,
    handleChange,
    fileUrl,
    onImageChange,
    clearImage,
    imgError,
    selectedFile,
    handleTabChange,
    tabValue,
    successAlertMessage,
    successAlertShow,
    errors,
    imgError,
    errorPopup,
    errorPopupMessage,
    setErrorPopup,
    goToLogin,
    buttonText,
    statusValue,
  };
  return (
    <AddProductContext.Provider value={providerValue}>
      {children}
    </AddProductContext.Provider>
  );
};
